import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { setApi } from "@/api/setApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { Slider } from "@/components/ui/slider";
import CustomToggleSelect from "@/components/ui/CustomToggleSelect";
import { TrashIcon, ArrowLeftIcon } from "lucide-react";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";
import { useTrackedExercises } from "@/hooks/useTrackedExercises";
import { useUserInfo } from "@/hooks/useUserInfo";
import { useMyWorkouts } from "@/hooks/useMyWorkouts";
import { TopNavByDates } from "@/components/workouts/TopNavByDates";
import {
  displayClaimedHourMinOnly,
  convertTo24Hour,
  displayLocalDate,
} from "@/lib/dateUtils";
import { SetModelJsonResource } from "@/types/setTypes";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";

interface ActiveDates {
  later_dates: string[];
  current: string;
  earlier_dates: string[];
}

export default function WorkoutSetEditPage() {
  const { nano_id } = useParams<{ nano_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { handleSuccess, handleError } = useToastHandler();
  const invalidate = useInvalidateResources();

  // Get set data from navigation state (primary method)
  const navigationSetData = location.state?.set as
    | SetModelJsonResource
    | undefined;
  const navigationWorkout = location.state?.workout as
    | WorkoutModelJsonResource
    | undefined;

  // Try to find the set and workout in existing cache (secondary method)
  const cachedData = useMemo(() => {
    if (navigationSetData || !nano_id) return { setData: null, workout: null };

    // Look through all cached workout data to find the set and its workout
    const cacheKeys = queryClient
      .getQueryCache()
      .getAll()
      .map((query) => query.queryKey)
      .filter((key) => Array.isArray(key) && key[0] === "/api/workouts");

    for (const key of cacheKeys) {
      const data = queryClient.getQueryData(key) as
        | {
            sets?: SetModelJsonResource[];
            workouts?: WorkoutModelJsonResource[];
          }
        | undefined;

      if (data?.sets) {
        const foundSet = data.sets.find(
          (set: SetModelJsonResource) => set.nano_id === nano_id
        );

        if (foundSet) {
          // Try to find the workout that contains this set
          let workout = null;
          if (data.workouts) {
            // Find the workout that contains this set
            workout =
              data.workouts.find((workout) =>
                workout.sets?.some((set) => set.nano_id === nano_id)
              ) || null;
          }

          return { setData: foundSet, workout };
        }
      }
    }
    return { setData: null, workout: null };
  }, [navigationSetData, nano_id, queryClient]);

  const cachedSetData = cachedData.setData;
  const cachedWorkout = cachedData.workout;

  // Fallback API call (tertiary method) - only if set not found in state or cache
  const shouldFetchFromAPI = !navigationSetData && !cachedSetData && !!nano_id;

  const {
    data: apiSetData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`/api/sets/${nano_id}`],
    queryFn: () => setApi.getSet(nano_id!),
    enabled: shouldFetchFromAPI,
  });

  // Determine which set data to use (priority: navigation > cache > API)
  const setData = navigationSetData || cachedSetData || apiSetData;

  // Determine which workout to use (priority: navigation > cache)
  const workout = navigationWorkout || cachedWorkout;

  // Extract workout title for display
  const workoutTitle = workout?.title || "Edit Set";

  const backPath = "/workouts/date/" + setData?.claimed_train_local_date;

  // Form state
  const [editWeight, setEditWeight] = useState(0);
  const [editReps, setEditReps] = useState(1);
  const [editUnit, setEditUnit] = useState<"lb" | "kg">("lb");
  const [editExerciseName, setEditExerciseName] = useState(
    setData?.exercise?.name || ""
  );
  const [editExerciseNanoId, setEditExerciseNanoId] = useState<
    string | undefined
  >(setData?.exercise?.nano_id || undefined);
  const [hasUserModifiedWeight, setHasUserModifiedWeight] = useState(false);
  const [editTrainAtTime, setEditTrainAtTime] = useState("");

  // Get tracked exercises for the combobox
  const { data: trackedExercises, isLoading: exercisesLoading } =
    useTrackedExercises();

  // Get user info for preferred weight unit
  const { data: user } = useUserInfo();

  // Date management for TopNavByDates
  const [currentDate, setCurrentDate] = useState<string | undefined>(undefined);

  // Get workout data for TopNavByDates
  const { data: workoutData } = useMyWorkouts(currentDate);

  // Cache previous active dates for TopNavByDates
  const previousActiveDatesRef = useRef<ActiveDates | undefined>(undefined);

  useEffect(() => {
    if (
      workoutData?.workouts &&
      workoutData?.workouts.length > 0 &&
      workoutData?.active_dates
    ) {
      previousActiveDatesRef.current = workoutData.active_dates;
    }
  }, [workoutData?.workouts, workoutData?.active_dates]);

  const activeDatesForTopNav =
    workoutData?.active_dates ?? previousActiveDatesRef.current;

  // Initialize form when data is available
  useEffect(() => {
    if (setData && user) {
      const userUnit = user.preferred_weight_unit;
      const weight = userUnit === "lb" ? setData.weight_lb : setData.weight_kg;

      setEditWeight(weight || 0);
      setEditReps(setData.reps || 1);
      setEditUnit(userUnit);
      setEditExerciseName(setData.exercise?.name || "");
      setEditExerciseNanoId(setData.exercise?.nano_id || "");

      // Initialize training time from claimed_train_at
      if (setData.claimed_train_at) {
        // Convert to 24-hour format for input (HH:mm)
        const timeStr = displayClaimedHourMinOnly(setData.claimed_train_at);
        // Convert from "08:12 AM" format to "08:12" format for input
        const time24 = convertTo24Hour(timeStr);
        setEditTrainAtTime(time24);
      }

      // Set current date from set data if not already set
      if (!currentDate && setData.claimed_train_local_date) {
        setCurrentDate(setData.claimed_train_local_date);
      }
    }
  }, [setData, user, currentDate]);

  // Prepare exercise options for combobox
  const exerciseOptions = useMemo(() => {
    if (!trackedExercises) return [];
    return trackedExercises.map((exercise) => ({
      id: exercise.nano_id,
      label: exercise.name,
    }));
  }, [trackedExercises]);

  // Handle exercise selection
  const handleExerciseChange = (id: number | string, label: string) => {
    setEditExerciseNanoId(String(id));
    setEditExerciseName(label);
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      setNanoId,
      weight,
      reps,
      unit,
      trainAtTime,
      exerciseNanoId,
      exerciseName,
    }: {
      setNanoId: string;
      weight: number;
      reps: number;
      unit: string;
      trainAtTime?: string;
      exerciseNanoId?: string;
      exerciseName?: string;
    }) =>
      setApi.updateSet(
        setNanoId,
        weight,
        reps,
        unit,
        trainAtTime,
        exerciseNanoId,
        exerciseName
      ),
    onSuccess: () => {
      handleSuccess("Set updated successfully.");
      invalidate([
        "/api/sets/recent",
        "/api/exercises/tracked",
        "/api/workouts",
        "/api/stats/overview",
        "/api/auth/me",
      ]);
      navigate(backPath);
    },
    onError: () => {
      handleError("Failed to update set.");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (setNanoId: string) => setApi.deleteSet(setNanoId),
    onSuccess: () => {
      handleSuccess("Set deleted successfully.");
      invalidate([
        "/api/sets/recent",
        "/api/exercises/tracked",
        "/api/workouts",
        "/api/stats/overview",
        "/api/auth/me",
      ]);
      navigate(backPath);
    },
    onError: () => {
      handleError("Failed to delete set.");
    },
  });

  const handleSave = () => {
    if (!setData) return;

    if (editWeight <= 0 || editReps <= 0) {
      handleError("Please enter valid weight and reps.");
      return;
    }

    if (!editExerciseName.trim()) {
      handleError("Please enter an exercise name.");
      return;
    }

    updateMutation.mutate({
      setNanoId: setData.nano_id,
      weight: editWeight,
      reps: editReps,
      unit: editUnit,
      trainAtTime: editTrainAtTime || undefined,
      exerciseNanoId: editExerciseNanoId || undefined,
      exerciseName: editExerciseName.trim(),
    });
  };

  const handleDelete = () => {
    if (!setData) return;

    deleteMutation.mutate(setData.nano_id);
  };

  // Show loading state while fetching from API
  if (shouldFetchFromAPI && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Show error state if API call failed
  if (shouldFetchFromAPI && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-500">Failed to load set data</p>
        <Button onClick={() => navigate(backPath)}>Back to Workouts</Button>
      </div>
    );
  }

  // Don't render if we don't have set data
  if (!setData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bg-base">
      {/* Date Navigation */}
      <TopNavByDates
        activeDates={activeDatesForTopNav}
        currentDate={currentDate}
        setCurrentDate={(clickedDate) => {
          // Navigate to workouts page for the selected date
          navigate(`/workouts/date/${clickedDate}`);
        }}
      />

      {/* Header */}
      <div className="max-w-md mx-auto px-4 mt-5 my-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 p-2 h-auto"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <h1 className="text-lg font-medium text-tx-default">
            {workoutTitle}
          </h1>
        </Button>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4">
        {/* Edit Form */}
        <div className="bg-white rounded-lg border border-bd-overlay p-6 space-y-6">
          {/* Logged Time */}
          <div>
            <label
              htmlFor="editTrainAtTime"
              className="block mb-2 text-sm font-medium text-tx-dark"
            >
              Logged Time
            </label>
            <div className="relative">
              <Input
                type="time"
                id="editTrainAtTime"
                name="editTrainAtTime"
                value={editTrainAtTime}
                onChange={(e) => setEditTrainAtTime(e.target.value)}
                className="w-full p-3 rounded-lg text-tx-dark border border-bd-light"
              />
              <span className="absolute top-3 right-3 text-base text-tx-muted">
                {displayLocalDate(setData?.claimed_train_local_date)}
              </span>
            </div>
          </div>

          {/* Exercise Name Input */}
          <div>
            <label
              htmlFor="editExercise"
              className="block mb-2 text-sm font-medium text-tx-dark"
            >
              Exercise
            </label>
            {exercisesLoading ? (
              <div className="flex items-center justify-center p-3 border rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                Loading exercises...
              </div>
            ) : (
              <Combobox
                options={exerciseOptions}
                value={editExerciseNanoId || ""}
                onChange={handleExerciseChange}
                onCreateOption={(newExerciseName) => {
                  const tempId = `new-${Date.now()}`;
                  handleExerciseChange(tempId, newExerciseName);
                }}
                placeholder="Select or type to create new exercise"
              />
            )}
          </div>

          {/* Weight and Unit */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label
                htmlFor="editWeight"
                className="mb-2 text-sm font-medium flex items-center text-tx-dark"
              >
                Weight
              </label>
              <Input
                type="number"
                id="editWeight"
                name="editWeight"
                min="0"
                step="0.1"
                value={editWeight === 0 ? "" : editWeight}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setEditWeight(value);
                  setHasUserModifiedWeight(true); // Mark that user has modified the weight
                }}
                className="w-full p-3 rounded-lg text-tx-dark border border-bd-light"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                placeholder="E.g.: 50"
                onFocus={(e) => e.target.select()}
                required
              />
            </div>

            {/* Unit */}
            <div className="flex-1">
              <label
                htmlFor="editUnit"
                className="block mb-2 text-sm font-medium text-tx-dark"
              >
                Unit
              </label>
              <CustomToggleSelect
                value={editUnit}
                className="w-full"
                setValue={(unit) => {
                  const newUnit = unit as "lb" | "kg";
                  if (
                    newUnit !== editUnit &&
                    setData &&
                    !hasUserModifiedWeight
                  ) {
                    // Only switch weight if user hasn't manually modified it
                    const weight =
                      newUnit === "lb" ? setData.weight_lb : setData.weight_kg;
                    setEditWeight(weight || 0);
                  }
                  setEditUnit(newUnit);
                }}
                options={[
                  { label: "lb", value: "lb" },
                  { label: "kg", value: "kg" },
                ]}
              />
            </div>
          </div>

          {/* Reps Slider */}
          <div className="flex flex-col gap-2 pb-6">
            <div className="flex justify-between mb-2">
              <label
                htmlFor="editReps"
                className="text-sm font-medium text-tx-dark"
              >
                Reps
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={editReps}
                onChange={(e) => {
                  const value = Math.min(
                    100,
                    Math.max(1, Number(e.target.value) || 1)
                  );
                  setEditReps(value);
                }}
                className="text-center pl-2 py-0 text-sm font-medium text-tx-primary border-b border-bd-light"
                style={{
                  background: "transparent",
                  outline: "none",
                }}
                onFocus={(e) => e.target.select()}
                aria-label="Edit reps value"
              />
            </div>
            <Slider
              min={1}
              max={20}
              step={1}
              value={[editReps > 20 ? 20 : editReps]}
              onValueChange={([value]) => setEditReps(value)}
              className="w-full"
            />
            <div className="flex justify-between mt-1 text-xs text-tx-muted">
              <span>1</span>
              <span>10</span>
              <span>20+</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(backPath)}
              disabled={updateMutation.isPending || deleteMutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSave}
              disabled={updateMutation.isPending || deleteMutation.isPending}
              className="flex-1"
            >
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Delete Button */}
        <div className="mt-10 border-t border-bd-overlay">
          <Button
            variant="destructive"
            size="lg"
            onClick={handleDelete}
            disabled={deleteMutation.isPending || updateMutation.isPending}
            className="w-full"
          >
            {deleteMutation.isPending ? (
              "Deleting..."
            ) : (
              <>
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete Set
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
